/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { EventEmitter, Injectable } from '@angular/core';
import invariant from 'invariant';

import { forEachRight, pull } from 'lodash';
import { NavigationItem, NavigationItemTree, NavigationNode } from './navigation.interfaces';

const ROOT_NODE = '';

@Injectable()
export class NavigationService {
  readonly tree: NavigationNode = {
    path: ROOT_NODE,
    item: { name: ROOT_NODE, title: '', order: 0 },
    parent: void 0,
    children: [],
  };
  readonly onChanged = new EventEmitter<NavigationNode>();

  private index: { [path: string]: NavigationNode } = {
    [ROOT_NODE]: this.tree,
  };

  addItem(item: NavigationItem): void;
  addItem(parentPath: string, item: NavigationItem): void;
  addItem(parentPath: string | NavigationItem, item?: NavigationItem): void {
    if (typeof parentPath !== 'string') {
      item = parentPath;
      parentPath = ROOT_NODE;
    }

    const parent: NavigationNode = this.index[parentPath];

    invariant(parent, `Parent navigation item for path '${parentPath}' does not exist`);
    invariant(item, `Navigation item is undefined`);

    if (item) {
      this._addItem(item, parent);

      this.onChanged.emit(this.tree);
    }
  }

  addItemTree(tree: NavigationItemTree): void;
  addItemTree(parentPath: string, tree: NavigationItemTree): void;
  addItemTree(parentPath: string | NavigationItemTree, tree?: NavigationItemTree): void {
    if (typeof parentPath !== 'string') {
      tree = parentPath;
      parentPath = ROOT_NODE;
    }

    const parent: NavigationNode = this.index[parentPath];

    invariant(parent, `Parent navigation item for path '${parentPath}' does not exist`);
    invariant(tree, `Navigation item is undefined`);

    if (!tree) {
      return;
    }

    const stack: NavigationItemTreeAndParent[] = [];

    stack.push({
      tree,
      parent,
    });

    while (stack.length) {
      const current = stack.pop();

      if (current && current.tree.enabled !== false) {
        const node = this._addItem(current.tree, current.parent);

        if (current.tree.children) {
          forEachRight(current.tree.children, (child) => stack.push({
            tree: child,
            parent: node,
          }));
        }
      }
    }

    this.onChanged.emit(this.tree);
  }

  removeItem(path: string): void {
    const node = this.index[path];

    invariant(node, `Navigation item for '${path}' does not exist`);

    this._removeItem(node);

    this.onChanged.emit(this.tree);
  }

  insertItemBefore(siblingName: string, item: NavigationItem, parentPath: string = ROOT_NODE): void {
    const parent: NavigationNode = this.index[parentPath];

    invariant(parent, `Parent navigation item for path '${parentPath}' does not exist`);
    invariant(item, `Navigation item is undefined`);

    const siblingIndex = parent.children.findIndex((child) => child.item.name === siblingName);

    if (siblingIndex === -1) {
      this._addItem(item, parent);
    } else {
      const path = parent.path ? `${parent.path}/${item.name}` : item.name;
      const node = { path, item, parent, children: [] };

      parent.children.splice(siblingIndex, 0, node);
      this.index[path] = node;
    }

    this.onChanged.emit(this.tree);
  }

  private _addItem(item: NavigationItem, parent: NavigationNode): NavigationNode {
    const path = parent.path ? `${parent.path}/${item.name}` : item.name;

    const node = { path, item, parent, children: [] };
    parent.children.push(node);
    this.index[path] = node;

    return node;
  }

  private _removeItem(node: NavigationNode): void {
    delete this.index[node.path];

    const parent = node.parent;

    if (parent) {
      pull(parent.children, node);
      node.parent = undefined;
    }

    node.children.forEach((child) => this._removeItem(child));
  }
}

class NavigationItemTreeAndParent {
  constructor(public tree: NavigationItemTree,
              public parent: NavigationNode) {
  }
}
